import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import {
    QUERY_SELECTED_FISH_GROUP_ID,
    readSelectedFishGroupId,
} from '@/contexts/cage-filter-context';
import { useGetClientOverview } from '@/domain/client';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { resolvePenFishGroupDecision } from '@/utils/resolvePenCohortName';
import { createLocationUrl } from '@/utils/urlGenerator';

/**
 * Pen pages that show no data scoped to a fish group: the pen's tools (winch, bioscope) and the
 * pages that are the location's or the client's. An empty pen still needs them, so the guard
 * leaves them alone.
 */
const PEN_PAGES_WITHOUT_FISH_GROUP = new Set([
    'move-bioscope',
    'bioscope',
    'change-pen',
    'compare',
    'users',
    'help',
]);

/** Whether a pen page, given as its path below the pen (`settings`, `weight/graphs`), is one. */
function isPenPageWithoutFishGroup(penPage: string): boolean {
    return PEN_PAGES_WITHOUT_FISH_GROUP.has(penPage.split('/')[0] ?? '');
}

interface PenFishGroupGuardProps {
    clientId: number;
    locationId: number;
    cageId: number;
    /** The path below the pen, the `*` of `pen/:cageId/*`. */
    penPage: string;
    children: ReactNode;
}

/**
 * Keeps a pen page's `fg` in step with the pen, with fish groups on.
 *
 * The pen pages scope every request by the `fg` in the URL, and send nothing when it is absent:
 * the data is then the cage's, across every group it has held. So a pen with a group in it gets
 * that group's `fg` back when it is missing, and a pen with nothing to show — an empty pen
 * reached without `fg`, or an `fg` that is not this pen's — sends the reader to the location.
 * A historical pen predates fish groups, so without `fg` it opens with the cage's data.
 *
 * Mounted outside `CagePageWrapper` so the pages never render, and never fire a cage-wide
 * request, before the URL is settled. The answer comes from the client overview, which `AppShell`
 * has almost always loaded already for the breadcrumb.
 */
export function PenFishGroupGuard(props: PenFishGroupGuardProps) {
    const { clientId, locationId, cageId, penPage, children } = props;
    const routerLocation = useLocation();
    const fishGroupTracking = usePostHogFeatureFlag('fish-group-tracking');

    // Same options as `PenCohortProvider`, so the two share one cached query.
    const { data: clientOverview, error } = useGetClientOverview(clientId, {
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        enabled: fishGroupTracking.enabled,
    });

    if (isPenPageWithoutFishGroup(penPage)) {
        return children;
    }
    if (fishGroupTracking.isLoading) {
        return null;
    }
    // A failed lookup is not an empty pen: the pages show their own errors, unscoped as before.
    if (!fishGroupTracking.enabled || error || !Number.isInteger(cageId)) {
        return children;
    }
    if (clientOverview == null) {
        return null;
    }

    const location = clientOverview.locations.find(
        (candidate) => candidate.locationId === locationId
    );
    if (!location) {
        return children;
    }
    // Only a response that asked for fish groups carries `closedFishGroups`. Judging any other
    // would read every pen as empty and send the reader away.
    if (location.closedFishGroups === undefined) {
        return null;
    }

    const decision = resolvePenFishGroupDecision(
        location,
        cageId,
        readSelectedFishGroupId(routerLocation.search)
    );

    if (decision.kind === 'leave') {
        return <Navigate to={createLocationUrl(clientId, locationId)} replace />;
    }
    if (decision.kind === 'select') {
        const search = new URLSearchParams(routerLocation.search);
        search.set(QUERY_SELECTED_FISH_GROUP_ID, String(decision.fishGroupId));
        return (
            <Navigate
                to={{ pathname: routerLocation.pathname, search: `?${search.toString()}` }}
                replace
            />
        );
    }
    return children;
}
